<template>
  <div class="merge-wrapper">
    <h2 class="section-title">GIF 合并</h2>

    <!-- 加载提示 -->
    <loading-tip 
      :visible="isLoading" 
      :text="loadingText" 
      :subtext="loadingSubtext"
      :progress="loadingProgress"
    />

    <!-- 上传区域 -->
    <label class="drop-area" :class="{ hover: isDragging }" @dragover.prevent="isDragging = true" @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop">
      拖拽或点击上传 GIF（可多选）
      <div class="size-hint">{{ isMobile ? '单个文件建议不超过 8MB' : '单个文件建议不超过 20MB' }}</div>
      <input type="file" ref="gifInput" accept="image/gif" multiple @change="handleFileChange">
    </label>

    <!-- GIF 列表 -->
    <div v-if="gifList.length" class="gif-list">
      <div class="list-header">
        <span class="list-title">已添加 {{ gifList.length }} 个 GIF</span>
        <button class="btn-clear" @click="clearAll">清空全部</button>
      </div>
      
      <div class="gif-items">
        <div 
          v-for="(gif, index) in gifList" 
          :key="gif.id" 
          class="gif-item"
          draggable="true"
          @dragstart="onDragStart(index)"
          @dragover.prevent="onDragOver(index)"
          @dragend="onDragEnd"
          :class="{ dragging: dragIndex === index }"
        >
          <div class="gif-order">{{ index + 1 }}</div>
          <img :src="gif.url" class="gif-thumb" alt="">
          <div class="gif-info">
            <div class="gif-name">{{ gif.name }}</div>
            <div class="gif-meta">{{ gif.frames }} 帧 · {{ formatSize(gif.size) }}</div>
          </div>
          <div class="gif-actions">
            <button class="btn-move" @click="moveUp(index)" :disabled="index === 0">↑</button>
            <button class="btn-move" @click="moveDown(index)" :disabled="index === gifList.length - 1">↓</button>
            <button class="btn-remove" @click="removeGif(index)">×</button>
          </div>
        </div>
      </div>
      
      <div class="drag-hint">💡 可拖拽调整顺序</div>
    </div>

    <!-- 合并选项 -->
    <div v-if="gifList.length >= 2" class="merge-options">
      <div class="option-title">合并选项</div>
      
      <div class="option-group">
        <label class="option-label">输出尺寸</label>
        <div class="size-options">
          <label class="radio-label">
            <input type="radio" v-model="sizeMode" value="first"> 使用第一个 GIF 尺寸
          </label>
          <label class="radio-label">
            <input type="radio" v-model="sizeMode" value="max"> 使用最大尺寸
          </label>
          <label class="radio-label">
            <input type="radio" v-model="sizeMode" value="custom"> 自定义尺寸
          </label>
        </div>
        <div v-if="sizeMode === 'custom'" class="custom-size">
          <input type="number" v-model.number="customWidth" min="1" class="size-input" placeholder="宽">
          <span>×</span>
          <input type="number" v-model.number="customHeight" min="1" class="size-input" placeholder="高">
        </div>
      </div>

      <div class="option-group">
        <label class="option-label">帧间过渡</label>
        <div class="size-options">
          <label class="radio-label">
            <input type="radio" v-model="transitionMode" value="none"> 无过渡
          </label>
          <label class="radio-label">
            <input type="radio" v-model="transitionMode" value="gap"> 添加间隔
          </label>
        </div>
        <div v-if="transitionMode === 'gap'" class="gap-setting">
          <label>间隔时间: {{ gapDuration }} ms</label>
          <input type="range" v-model.number="gapDuration" min="100" max="2000" step="100" class="range-input">
        </div>
      </div>

      <div class="merge-info">
        <span>预计总帧数: {{ totalFrames }} 帧</span>
      </div>
    </div>

    <!-- 操作按钮 -->
    <div v-if="gifList.length >= 2" class="action-row">
      <button class="btn-primary" :disabled="isMerging" @click="startMerge">
        {{ isMerging ? '正在合并...' : '开始合并' }}
      </button>
      <button class="btn-download" :disabled="!resultGifUrl" @click="downloadGif">下载 GIF</button>
    </div>

    <!-- 进度条 -->
    <div class="progress-container" v-if="isMerging">
      <div class="progress-label">正在合并 GIF... {{ mergeProgress }}%</div>
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: mergeProgress + '%' }"></div>
      </div>
    </div>

    <!-- 结果预览 -->
    <div v-if="resultGifUrl" class="result-section">
      <div class="result-title">合并结果</div>
      <img :src="resultGifUrl" alt="合并后的 GIF" class="result-gif">
    </div>
  </div>
</template>

<script>
import SuperGif from '../lib/libgif.js'
import GIF from 'gif.js'
import LoadingTip from './LoadingTip.vue'
import { isMobile, getDeviceConfig, checkFileSize, formatFileSize, yieldToMain, downloadFile, blobToBase64 } from './utils.js'

export default {
  name: 'GifMerge',
  components: {
    LoadingTip
  },
  data() {
    const config = getDeviceConfig()
    return {
      gifList: [],
      isDragging: false,
      dragIndex: -1,
      dropIndex: -1,
      
      // 合并选项
      sizeMode: 'first',
      customWidth: 300,
      customHeight: 300,
      transitionMode: 'none',
      gapDuration: 500,
      
      // 合并状态
      isMerging: false,
      mergeProgress: 0,
      resultGifUrl: '',
      
      nextId: 0,
      
      // 加载状态
      isLoading: false,
      loadingText: '',
      loadingSubtext: '',
      loadingProgress: -1,
      
      // 设备配置
      isMobile: config.isMobile,
      maxGifSize: config.maxGifSize,
      batchSize: config.batchSize
    }
  },
  computed: {
    totalFrames() {
      let total = this.gifList.reduce((sum, gif) => sum + gif.frames, 0)
      if (this.transitionMode === 'gap' && this.gifList.length > 1) {
        total += this.gifList.length - 1 // 间隔帧
      }
      return total
    }
  },
  methods: {
    formatSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    },
    handleDrop(e) {
      this.isDragging = false
      const files = Array.from(e.dataTransfer.files).filter(f => f.type === 'image/gif')
      files.forEach(file => this.addGif(file))
    },
    handleFileChange(e) {
      const files = Array.from(e.target.files)
      files.forEach(file => this.addGif(file))
      e.target.value = ''
    },
    addGif(file) {
      // 文件大小检查
      const check = checkFileSize(file, this.maxGifSize)
      if (!check.valid) {
        this.$toast(check.message)
        return
      }
      
      // 显示加载提示
      this.isLoading = true
      this.loadingText = '正在解析 GIF...'
      this.loadingSubtext = `文件: ${file.name}`
      this.loadingProgress = -1
      
      const gif = {
        id: this.nextId++,
        file: file,
        name: file.name.replace(/\.gif$/i, ''),
        size: file.size,
        url: URL.createObjectURL(file),
        frames: 0,
        frameDelays: [],
        width: 0,
        height: 0,
        loaded: false,
        // 保存 SuperGif 实例用于流式处理
        superGif: null,
        wrapper: null,
        blobUrl: null
      }
      
      this.gifList.push(gif)
      this.parseGifStream(gif)
    },
    parseGifStream(gif) {
      const wrapper = document.createElement('div')
      wrapper.style.cssText = 'position:fixed;left:-9999px;top:-9999px;'
      
      const gifImg = document.createElement('img')
      const blobUrl = gif.url
      gifImg.setAttribute('rel:animated_src', blobUrl)
      gifImg.setAttribute('rel:auto_play', '0')
      
      wrapper.appendChild(gifImg)
      document.body.appendChild(wrapper)
      
      const rub = new SuperGif({ gif: gifImg })
      rub.load(() => {
        const length = rub.get_length()
        gif.frames = length
        gif.frameDelays = []
        
        const canvas = rub.get_canvas()
        gif.width = canvas.width
        gif.height = canvas.height
        
        // 只获取帧延迟信息，不存储帧数据
        const framesData = rub.get_frames()
        for (let i = 0; i < length; i++) {
          let delay = 100
          if (framesData && framesData[i] && framesData[i].delay) {
            delay = framesData[i].delay * 10
          }
          gif.frameDelays.push(delay < 20 ? 100 : delay)
        }
        
        // 保存 SuperGif 实例
        gif.superGif = rub
        gif.wrapper = wrapper
        gif.blobUrl = blobUrl
        gif.loaded = true
        
        this.isLoading = false
        
        // 设置默认自定义尺寸
        if (this.gifList.length === 1) {
          this.customWidth = gif.width
          this.customHeight = gif.height
        }
        
        console.log(`GIF 解析完成: ${gif.name}, ${length} 帧`)
      })
    },
    removeGif(index) {
      const gif = this.gifList[index]
      if (gif.url) {
        URL.revokeObjectURL(gif.url)
      }
      if (gif.wrapper && gif.wrapper.parentNode) {
        gif.wrapper.parentNode.removeChild(gif.wrapper)
      }
      this.gifList.splice(index, 1)
    },
    clearAll() {
      this.gifList.forEach(gif => {
        if (gif.url) URL.revokeObjectURL(gif.url)
        if (gif.wrapper && gif.wrapper.parentNode) {
          gif.wrapper.parentNode.removeChild(gif.wrapper)
        }
      })
      this.gifList = []
      this.resultGifUrl = ''
      this.isLoading = false
    },
    moveUp(index) {
      if (index > 0) {
        const temp = this.gifList[index]
        this.$set(this.gifList, index, this.gifList[index - 1])
        this.$set(this.gifList, index - 1, temp)
      }
    },
    moveDown(index) {
      if (index < this.gifList.length - 1) {
        const temp = this.gifList[index]
        this.$set(this.gifList, index, this.gifList[index + 1])
        this.$set(this.gifList, index + 1, temp)
      }
    },
    onDragStart(index) {
      this.dragIndex = index
    },
    onDragOver(index) {
      if (this.dragIndex !== -1 && this.dragIndex !== index) {
        const temp = this.gifList[this.dragIndex]
        this.$set(this.gifList, this.dragIndex, this.gifList[index])
        this.$set(this.gifList, index, temp)
        this.dragIndex = index
      }
    },
    onDragEnd() {
      this.dragIndex = -1
    },
    getOutputSize() {
      if (this.sizeMode === 'custom') {
        return { width: this.customWidth, height: this.customHeight }
      }
      if (this.sizeMode === 'max') {
        let maxW = 0, maxH = 0
        this.gifList.forEach(gif => {
          if (gif.width > maxW) maxW = gif.width
          if (gif.height > maxH) maxH = gif.height
        })
        return { width: maxW, height: maxH }
      }
      // first
      const first = this.gifList[0]
      return { width: first.width, height: first.height }
    },
    async startMerge() {
      // 检查是否所有 GIF 都已加载
      const allLoaded = this.gifList.every(g => g.loaded)
      if (!allLoaded) {
        this.$toast('请等待所有 GIF 加载完成')
        return
      }
      
      this.isMerging = true
      this.mergeProgress = 0
      this.resultGifUrl = ''
      
      const { width, height } = this.getOutputSize()
      
      const gif = new GIF({
        workers: this.isMobile ? 1 : 2,
        quality: this.isMobile ? 15 : 10,
        workerScript: './static/gifjs/gif.worker.js',
        width: width,
        height: height
      })
      
      gif.on('progress', (p) => {
        this.mergeProgress = Math.round(50 + p * 50)
      })
      
      // 创建可复用的 canvas
      const tempCanvas = document.createElement('canvas')
      tempCanvas.width = width
      tempCanvas.height = height
      const tempCtx = tempCanvas.getContext('2d')
      
      gif.on('finished', async (blob) => {
        // 使用data URL而不是blob URL，这样在小程序webview中可以长按保存
        this.resultGifUrl = await blobToBase64(blob)
        this.isMerging = false
        this.mergeProgress = 100
        
        // 释放 canvas 内存
        tempCanvas.width = 1
        tempCanvas.height = 1
      })
      
      // 流式合并所有帧
      let processedFrames = 0
      const totalFrames = this.totalFrames
      
      for (let i = 0; i < this.gifList.length; i++) {
        const gifItem = this.gifList[i]
        
        if (!gifItem.superGif) continue
        
        // 添加该 GIF 的所有帧 - 流式读取
        for (let j = 0; j < gifItem.frames; j++) {
          gifItem.superGif.move_to(j + 1)
          const frameCanvas = gifItem.superGif.get_canvas()
          
          // 填充背景
          tempCtx.fillStyle = '#ffffff'
          tempCtx.fillRect(0, 0, width, height)
          
          // 居中绘制
          const scale = Math.min(width / frameCanvas.width, height / frameCanvas.height)
          const drawWidth = frameCanvas.width * scale
          const drawHeight = frameCanvas.height * scale
          const x = (width - drawWidth) / 2
          const y = (height - drawHeight) / 2
          
          tempCtx.drawImage(frameCanvas, x, y, drawWidth, drawHeight)
          
          gif.addFrame(tempCanvas, { copy: true, delay: gifItem.frameDelays[j] || 100 })
          
          processedFrames++
          this.mergeProgress = Math.round((processedFrames / totalFrames) * 50)
          
          // 每处理几帧让出主线程
          if (j % this.batchSize === 0) {
            await yieldToMain()
          }
        }
        
        // 添加过渡间隔帧
        if (this.transitionMode === 'gap' && i < this.gifList.length - 1) {
          // 使用当前 GIF 最后一帧作为间隔帧
          gifItem.superGif.move_to(gifItem.frames)
          const lastFrameCanvas = gifItem.superGif.get_canvas()
          
          tempCtx.fillStyle = '#ffffff'
          tempCtx.fillRect(0, 0, width, height)
          
          const scale = Math.min(width / lastFrameCanvas.width, height / lastFrameCanvas.height)
          const drawWidth = lastFrameCanvas.width * scale
          const drawHeight = lastFrameCanvas.height * scale
          const x = (width - drawWidth) / 2
          const y = (height - drawHeight) / 2
          
          tempCtx.drawImage(lastFrameCanvas, x, y, drawWidth, drawHeight)
          
          gif.addFrame(tempCanvas, { copy: true, delay: this.gapDuration })
          processedFrames++
          this.mergeProgress = Math.round((processedFrames / totalFrames) * 50)
        }
      }
      
      gif.render()
    },
    // 保留旧方法用于兼容
    addFrame(gif, frameDataUrl, delay, width, height) {
      return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => {
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const ctx = canvas.getContext('2d')
          
          // 填充背景
          ctx.fillStyle = '#ffffff'
          ctx.fillRect(0, 0, width, height)
          
          // 居中绘制
          const scale = Math.min(width / img.width, height / img.height)
          const drawWidth = img.width * scale
          const drawHeight = img.height * scale
          const x = (width - drawWidth) / 2
          const y = (height - drawHeight) / 2
          
          ctx.drawImage(img, x, y, drawWidth, drawHeight)
          
          gif.addFrame(canvas, { copy: true, delay: delay })
          resolve()
        }
        img.src = frameDataUrl
      })
    },
    async downloadGif() {
      if (!this.resultGifUrl) return
      await downloadFile(this.resultGifUrl, 'merged.gif')
    }
  },
  beforeDestroy() {
    this.gifList.forEach(gif => {
      if (gif.url) URL.revokeObjectURL(gif.url)
    })
    if (this.resultGifUrl) {
      URL.revokeObjectURL(this.resultGifUrl)
    }
  }
}
</script>

<style>
.merge-wrapper {
  padding: 8px 0;
}

.merge-wrapper .section-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #1f2933;
}

.merge-wrapper .drop-area {
  display: flex;
  flex-direction: column;
  padding: 40px 30px;
  min-height: 120px;
  border: 2px dashed #9fb3ff;
  border-radius: 12px;
  background: #f8f9ff;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 16px;
  justify-content: center;
  align-items: center;
  font-size: 15px;
  color: #4f46e5;
  font-weight: 500;
}

.merge-wrapper .drop-area .size-hint {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 8px;
  font-weight: 400;
}

.merge-wrapper .drop-area.hover {
  border-color: #667eea;
  background: #e5edff;
}

.merge-wrapper .drop-area input[type="file"] {
  display: none;
}

.merge-wrapper .gif-list {
  background: #f8f9ff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.merge-wrapper .list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.merge-wrapper .list-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.merge-wrapper .btn-clear {
  padding: 4px 12px;
  font-size: 12px;
  border-radius: 6px;
  border: 1px solid #dc3545;
  background: #fff;
  color: #dc3545;
  cursor: pointer;
}

.merge-wrapper .gif-items {
  max-height: 300px;
  overflow-y: auto;
}

.merge-wrapper .gif-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: grab;
  transition: all 0.2s;
  border: 1px solid #e5e7eb;
}

.merge-wrapper .gif-item:hover {
  border-color: #667eea;
}

.merge-wrapper .gif-item.dragging {
  opacity: 0.5;
  transform: scale(0.98);
}

.merge-wrapper .gif-order {
  width: 24px;
  height: 24px;
  background: #667eea;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.merge-wrapper .gif-thumb {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
}

.merge-wrapper .gif-info {
  flex: 1;
  min-width: 0;
}

.merge-wrapper .gif-name {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.merge-wrapper .gif-meta {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
}

.merge-wrapper .gif-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.merge-wrapper .btn-move {
  width: 28px;
  height: 28px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #fff;
  color: #6b7280;
  cursor: pointer;
  font-size: 14px;
}

.merge-wrapper .btn-move:hover:not(:disabled) {
  border-color: #667eea;
  color: #667eea;
}

.merge-wrapper .btn-move:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.merge-wrapper .btn-remove {
  width: 28px;
  height: 28px;
  border: 1px solid #dc3545;
  border-radius: 4px;
  background: #fff;
  color: #dc3545;
  cursor: pointer;
  font-size: 16px;
}

.merge-wrapper .drag-hint {
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
  margin-top: 8px;
}

.merge-wrapper .merge-options {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.merge-wrapper .option-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 16px;
}

.merge-wrapper .option-group {
  margin-bottom: 14px;
}

.merge-wrapper .option-label {
  display: block;
  font-size: 13px;
  color: #4b5563;
  margin-bottom: 8px;
}

.merge-wrapper .size-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.merge-wrapper .radio-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #4b5563;
  cursor: pointer;
}

.merge-wrapper .custom-size {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding-left: 20px;
}

.merge-wrapper .size-input {
  width: 70px;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  text-align: center;
}

.merge-wrapper .gap-setting {
  margin-top: 8px;
  padding-left: 20px;
}

.merge-wrapper .gap-setting label {
  font-size: 12px;
  color: #6b7280;
}

.merge-wrapper .range-input {
  width: 100%;
  margin-top: 6px;
}

.merge-wrapper .merge-info {
  font-size: 12px;
  color: #6b7280;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
}

.merge-wrapper .action-row {
  display: flex;
  gap: 10px;
  margin: 16px 0;
}

.merge-wrapper .btn-primary {
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.merge-wrapper .btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.merge-wrapper .btn-download {
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  background: #28a745;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.merge-wrapper .btn-download:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.merge-wrapper .progress-container {
  margin: 16px 0;
  padding: 12px;
  background: #f8f9ff;
  border-radius: 8px;
}

.merge-wrapper .progress-label {
  font-size: 13px;
  color: #4f46e5;
  font-weight: 500;
  margin-bottom: 8px;
  text-align: center;
}

.merge-wrapper .progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.merge-wrapper .progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.merge-wrapper .result-section {
  margin-top: 16px;
  text-align: center;
}

.merge-wrapper .result-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
}

.merge-wrapper .result-gif {
  max-width: 100%;
  max-height: 300px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
}
</style>

